import React, { useMemo } from "react";
import { CCard, CCardHeader, CCardBody, CRow, CCol, CListGroup, CListGroupItem, CTooltip } from "@coreui/react";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveLine } from "@nivo/line";
import { useTranslation } from "react-i18next";
import InteractiveTimeline from "../InteractiveTimeline/InteractiveTimeline";
import { formatDuration } from "../../utils/cameraUtils";

const AnalyticsBlock = React.memo(({ title, analyticsData, classIdToMeta, onSegmentDoubleClick, timelineDataOverride, type = "general", operatingMode, dateRangeOverride }) => {
  const { t } = useTranslation();

  // Динамические заголовки и единицы измерения в зависимости от типа вкладки
  const getLabels = () => {
    // 1. Клиенты в холодильнике без внимания
    if (type === "unattended_clients") {
      return {
        downtime: "Время: Сотрудник на месте / Нет ожидания",
        work: "Суммарное время ожидания клиентов (>1 мин)",
        max: "Макс. клиентов без внимания",
        detail: "Детализация по количеству ожидающих клиентов:",
        unit: "чел."
      };
    }
    // 2. Пустые места в холодильнике
    if (type === "fridge_empty" || (operatingMode === "fridge" && type !== "unattended_clients")) {
      return {
        downtime: "Время: Заполнено (нет пустых мест)",
        work: "Суммарное время пустых мест",
        max: "Макс. пустых мест в холодильнике",
        detail: "Детализация по количеству пустых мест:",
        unit: "мест"
      };
    }
    // 3. Чеки / Касса
    if (type === "receipts" || operatingMode === "receipt") {
      return {
        downtime: "Время: Без чеков/касс",
        work: "Суммарное время присутствия чеков/касс",
        max: "Макс. чеков/касс в кадре",
        detail: "Детализация по количеству чеков/касс:",
        unit: "шт."
      };
    }
    // 4. Стул
    if (type === "chair") {
      return {
        downtime: "Время: Все стоят / Не сидят",
        work: "Суммарное время на стуле",
        max: "Макс. людей на стульях",
        detail: "Детализация по количеству сидящих сотрудников:",
        unit: "чел."
      };
    }
    // 5. Телефон
    if (type === "phone") {
      return {
        downtime: "Время: Без телефонов",
        work: "Суммарное время в телефоне",
        max: "Макс. людей с телефонами",
        detail: "Детализация по количеству сотрудников с телефонами:",
        unit: "чел."
      };
    }
    // 6. Работа с цветами
    if (type === "work_flowers") {
      return {
        downtime: "Время: Без работы с цветами",
        work: "Суммарное время работы с цветами",
        max: "Макс. работающих с цветами",
        detail: "Детализация по количеству работающих с цветами:",
        unit: "чел."
      };
    }
    // 7. Без формы
    if (type === "no_uniform") {
      return {
        downtime: "Время: Все в форме",
        work: "Суммарное время без формы",
        max: "Макс. людей без формы",
        detail: "Детализация по количеству сотрудников без формы:",
        unit: "чел."
      };
    }
    // 8. По умолчанию (Общая активность персонала)
    return {
      downtime: "Отсутствие сотрудника / Простой",
      work: "Время работы (суммарное присутствие)",
      max: "Макс. людей на посту",
      detail: "Детализация по количеству сотрудников:",
      unit: "чел."
    };
  };

  const labels = getLabels();

  // Подготовка данных для круговой диаграммы
  const pieData = useMemo(() => {
    if (!analyticsData) return [];
    let data = [];
    let totalWorkSeconds = analyticsData.durations_by_people_count?.reduce((sum, item) => sum + (item.total_duration_seconds * item.people_count), 0) || 0;
    
    if (analyticsData.total_downtime_seconds > 0) {
      data.push({ id: "Downtime", label: labels.downtime, value: analyticsData.total_downtime_seconds, color: "#6c757d" });
    }
    
    if (totalWorkSeconds > 0) {
      let color = "#2eb85c"; // Зеленый по умолчанию
      if (type === "chair") color = "#3399ff";                // Синий для стула
      else if (type === "phone") color = "#ff9933";            // Оранжевый для телефона
      else if (type === "work_flowers") color = "#9c27b0";     // Фиолетовый для цветов
      else if (type === "no_uniform") color = "#ffb300";       // Желтый для формы
      else if (type === "unattended_clients") color = "#ff9800"; // Янтарный для ожидания клиентов
      else if (type === "fridge_empty" || operatingMode === "fridge") color = "#00bcd4"; // Голубой для холодильника
      else if (type === "receipts" || operatingMode === "receipt") color = "#f44336"; // Красный для чеков

      data.push({ id: "Working", label: labels.work, value: totalWorkSeconds, color });
    }
    return data;
  }, [analyticsData, labels, type, operatingMode]);

  const rawTimeline = timelineDataOverride || analyticsData.timeline;
  
  const startDateStr = analyticsData?.start_date || analyticsData?.date_range?.start;
  const endDateStr = analyticsData?.end_date || analyticsData?.date_range?.end;
  const fallbackStart = rawTimeline?.[0]?.start;
  const fallbackEnd = rawTimeline?.[rawTimeline?.length - 1]?.end;

  const timelineDateRange = useMemo(() => {
    if (dateRangeOverride) return dateRangeOverride;
    if (startDateStr && endDateStr) return { start: startDateStr, end: endDateStr };
    if (fallbackStart && fallbackEnd) return { start: fallbackStart, end: fallbackEnd };
    return null;
  }, [dateRangeOverride, startDateStr, endDateStr, fallbackStart, fallbackEnd]);

  const lineChartData = useMemo(() => {
    if (type !== "queue" || !rawTimeline) return [];
    const shiftToBrowserLocal = (isoString) => {
      if (!isoString) return 0;
      const d = new Date(isoString);
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours() + 3, d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds());
    };
    const dataPoints = [];
    rawTimeline.forEach((seg) => {
      dataPoints.push({ x: shiftToBrowserLocal(seg.start), y: seg.people_count });
      dataPoints.push({ x: shiftToBrowserLocal(seg.end), y: seg.people_count });
    });
    return [{ id: "queue", data: dataPoints }];
  }, [rawTimeline, type]);

  const hintStyle = { borderBottom: '1px dashed #999', cursor: 'help', width: 'fit-content' };

  return (
    <CCard className="mb-4 shadow-sm border-top-primary border-top-3">
      <CCardHeader className="bg-white"><strong>{title}</strong></CCardHeader>
      <CCardBody>
        <CRow>
          <CCol md={6}>
            <CListGroup flush>
              {/* Простой / Отсутствие */}
              <CListGroupItem>
                <CTooltip content={t("tooltips.downtime")}>
                  <strong style={hintStyle}>{labels.downtime}:</strong>
                </CTooltip> {formatDuration(analyticsData.total_downtime_seconds)}
              </CListGroupItem>
              
              {/* Время активности / ожидания */}
              <CListGroupItem>
                <CTooltip content={t("tooltips.total_work")}>
                  <strong style={hintStyle}>{labels.work}:</strong>
                </CTooltip> {formatDuration(analyticsData.durations_by_people_count?.reduce((sum, item) => sum + (item.total_duration_seconds * item.people_count), 0) || 0)}
              </CListGroupItem>
              
              {/* Максимальное количество */}
              <CListGroupItem>
                <CTooltip content={t("tooltips.max_people")}>
                  <strong style={hintStyle}>{labels.max}:</strong>
                </CTooltip> {analyticsData.max_concurrent_people}
              </CListGroupItem>

              {/* Детализация */}
              {analyticsData.durations_by_people_count?.length > 0 && (
                <div className="mt-2 pt-2 border-top">
                  <small className="text-muted d-block mb-1">{labels.detail}</small>
                  {analyticsData.durations_by_people_count.map((item) => {
                    let metaId;
                    if (type === "general") metaId = -100 - item.people_count;
                    else if (type === "chair") metaId = -300 - item.people_count;
                    else if (type === "phone") metaId = -400 - item.people_count;
                    else if (type === "work_flowers") metaId = -800 - item.people_count;
                    else if (type === "no_uniform") metaId = -900 - item.people_count;
                    else if (type === "fridge_empty") metaId = -1000 - item.people_count;
                    else if (type === "receipts") metaId = -1100 - item.people_count;
                    else if (type === "unattended_clients") metaId = -1200 - item.people_count;
                    else if (operatingMode === "fridge") metaId = -1000 - item.people_count;
                    else if (operatingMode === "receipt") metaId = -1100 - item.people_count;
                    else metaId = -100 - item.people_count;
                    
                    if (item.people_count >= 10) metaId = metaId - 1;
                    const meta = classIdToMeta[String(metaId)] || classIdToMeta["default"];
                    return (
                      <div key={item.people_count} className="d-flex justify-content-between small px-2 align-items-center">
                        <span>
                          <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: meta?.color || "#ccc", marginRight: "6px" }}></span>
                          {item.people_count} {labels.unit}:
                        </span>
                        <span>{formatDuration(item.total_duration_seconds * item.people_count)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CListGroup>
          </CCol>
          <CCol md={6}>
            {pieData.length > 0 ? (
              <div style={{ height: "200px" }}>
                <ResponsivePie 
                    data={pieData} 
                    margin={{ top: 10, right: 10, bottom: 10, left: 10 }} 
                    innerRadius={0.6} padAngle={1} cornerRadius={3} 
                    activeOuterRadiusOffset={8} colors={{ datum: "data.color" }} 
                    enableArcLabels={true} 
                    arcLabel={(d) => `${Math.round((d.value / pieData.reduce((acc, v) => acc + v.value, 0)) * 100)}%`} 
                    enableArcLinkLabels={false} 
                    tooltip={({ datum }) => (
                        <div style={{ padding: "5px 10px", background: "white", border: "1px solid #ccc", borderRadius: "4px", fontSize: "12px" }}>
                            <div style={{ width: "10px", height: "10px", background: datum.color, display: "inline-block", marginRight: "5px" }}></div>
                            <strong>{datum.label}</strong>: {formatDuration(datum.value)}
                        </div>
                    )} 
                />
              </div>
            ) : (<p className="text-muted text-center m-0 pt-5">{t("camera.no_videos")}</p>)}
          </CCol>
        </CRow>
        <h6 className="mt-4 mb-2 text-primary">{t("camera.timeline_local")}</h6>
        {rawTimeline ? (
            <InteractiveTimeline timelineData={rawTimeline} dateRange={timelineDateRange} onItemDoubleClick={onSegmentDoubleClick} classIdToMeta={classIdToMeta} />
        ) : (
            <p className="text-muted small">Нет данных для временной шкалы.</p>
        )}
        
        {type === "queue" && lineChartData[0]?.data.length > 0 && (
          <div className="mt-4 border-top pt-4">
            <h6 className="mb-2 text-primary">{t("camera.queue_chart_title")}</h6>
            <div style={{ height: "300px" }}>
                <ResponsiveLine 
                    data={lineChartData} 
                    margin={{ top: 20, right: 20, bottom: 50, left: 50 }} 
                    xScale={{ type: "time", format: "native" }} 
                    yScale={{ type: "linear", min: 0, max: "auto" }} 
                    curve="stepAfter" useMesh={true} colors={["#9c27b0"]} enableArea={true} areaOpacity={0.1} 
                />
            </div>
          </div>
        )}
      </CCardBody>
    </CCard>
  );
});

export default AnalyticsBlock;